import { useState, useEffect, useCallback } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { SmilePlus, Meh, Frown, MessageSquare, Building2, User, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import axios from "axios";
import Layout from "../components/Layout";
import { ScoreGauge, NPSBreakdownCard, ComparisonBadge, TrendCharts, FeedbackCard } from "../components/satisfaction/SatisfactionCards";


const SatisfactionPage = () => {
  const [npsData, setNpsData] = useState(null);
  const [csatData, setCsatData] = useState(null);
  const [npsTrends, setNpsTrends] = useState(null);
  const [csatTrends, setCsatTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [npsPeriod, setNpsPeriod] = useState("last_30_days");
  const [csatPeriod, setCsatPeriod] = useState("last_30_days");
  const [activeTab, setActiveTab] = useState("nps");
  const [npsFilter, setNpsFilter] = useState("all"); // all, Promoter, Passive, Detractor
  const [npsUserTypeFilter, setNpsUserTypeFilter] = useState("all"); // all, manager, provider
  const [csatFilter, setCsatFilter] = useState("all"); // all, positive, neutral, negative
  const [feedbackNotes, setFeedbackNotes] = useState({}); // Map of feedback_id -> notes

  const fetchFeedbackNotes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/feedback/notes`, { withCredentials: true });
      setFeedbackNotes(response.data);
    } catch (error) {
    }
  }, []);

  const fetchNpsData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/surveys/nps?period=${npsPeriod}`, { withCredentials: true });
      setNpsData(response.data);
    } catch (error) {
      // Error handled silently
      toast.error("Failed to fetch NPS data");
    }
  }, [npsPeriod]);

  const fetchCsatData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/surveys/csat?period=${csatPeriod}`, { withCredentials: true });
      setCsatData(response.data);
    } catch (error) {
      // Error handled silently
      toast.error("Failed to fetch CSAT data");
    }
  }, [csatPeriod]);

  const fetchNpsTrends = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/surveys/trends?survey_type=nps`, { withCredentials: true });
      setNpsTrends(response.data);
    } catch (error) {
    }
  }, []);

  const fetchCsatTrends = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/surveys/trends?survey_type=csat`, { withCredentials: true });
      setCsatTrends(response.data);
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchNpsData(), 
        fetchCsatData(), 
        fetchFeedbackNotes(),
        fetchNpsTrends(),
        fetchCsatTrends()
      ]);
      setLoading(false);
    };
    fetchAll();
  }, [fetchNpsData, fetchCsatData, fetchFeedbackNotes, fetchNpsTrends, fetchCsatTrends]);

  if (loading) {
    return (
      <Layout title="CSAT & NPS">
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="CSAT & NPS">
      <div className="space-y-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-lg">
            <TabsTrigger value="nps" className="data-[state=active]:bg-[#425B76] data-[state=active]:text-white rounded-md px-6">
              NPS
            </TabsTrigger>
            <TabsTrigger value="csat" className="data-[state=active]:bg-[#425B76] data-[state=active]:text-white rounded-md px-6">
              CSAT
            </TabsTrigger>
          </TabsList>

          {/* NPS Tab */}
          <TabsContent value="nps" className="mt-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Net Promoter Score</h2>
                <p className="text-slate-500">NPS - App Experience Feedback Survey</p>
              </div>
              <div className="flex items-center gap-3">
                <Select value={npsPeriod} onValueChange={setNpsPeriod}>
                  <SelectTrigger className="w-[180px] bg-white">
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="last_30_days">Last 30 Days</SelectItem>
                    <SelectItem value="last_3_months">Last 3 Months</SelectItem>
                    <SelectItem value="last_6_months">Last 6 Months</SelectItem>
                    <SelectItem value="last_12_months">Last 12 Months</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={fetchNpsData} className="border-slate-200">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* NPS Score Cards - Overall + Breakdown by User Type */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Overall NPS */}
              <NPSBreakdownCard 
                title="Overall"
                score={npsData?.score || 0}
                total={npsData?.total || 0}
                promoters={npsData?.promoters || 0}
                passives={npsData?.passives || 0}
                detractors={npsData?.detractors || 0}
              />
              
              {/* Manager NPS */}
              <NPSBreakdownCard 
                title="Manager"
                score={npsData?.breakdown?.manager?.score || 0}
                total={npsData?.breakdown?.manager?.total || 0}
                promoters={npsData?.breakdown?.manager?.promoters || 0}
                passives={npsData?.breakdown?.manager?.passives || 0}
                detractors={npsData?.breakdown?.manager?.detractors || 0}
              />
              
              {/* Provider NPS */}
              <NPSBreakdownCard 
                title="Provider"
                score={npsData?.breakdown?.provider?.score || 0}
                total={npsData?.breakdown?.provider?.total || 0}
                promoters={npsData?.breakdown?.provider?.promoters || 0}
                passives={npsData?.breakdown?.provider?.passives || 0}
                detractors={npsData?.breakdown?.provider?.detractors || 0}
              />
            </div>

            {/* NPS Trend Charts */}
            <TrendCharts trends={npsTrends} surveyType="nps" />

            {/* NPS Responses */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-[#425B76]" />
                    Recent NPS Responses
                  </CardTitle>
                  <div className="flex items-center gap-4 flex-wrap">
                    {/* User Type Filter */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">User Type:</span>
                      <div className="flex bg-slate-100 rounded-lg p-1 gap-1">
                        {[
                          { value: "all", label: "All" },
                          { value: "manager", label: "Manager", icon: Building2 },
                          { value: "provider", label: "Provider", icon: User }
                        ].map((f) => (
                          <button
                            key={f.value}
                            onClick={() => setNpsUserTypeFilter(f.value)}
                            className={`px-3 py-1.5 text-xs rounded-md transition-all flex items-center gap-1.5 ${
                              npsUserTypeFilter === f.value
                                ? "bg-white shadow-sm text-slate-900 font-medium"
                                : "text-slate-600 hover:text-slate-900"
                            }`}
                          >
                            {f.icon && <f.icon className="w-3 h-3" />}
                            {f.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Sentiment Filter */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">Sentiment:</span>
                      <div className="flex bg-slate-100 rounded-lg p-1 gap-1">
                        {[
                          { value: "all", label: "All" },
                          { value: "Promoter", label: "Promoters", color: "bg-emerald-500" },
                          { value: "Passive", label: "Passives", color: "bg-amber-500" },
                          { value: "Detractor", label: "Detractors", color: "bg-red-500" }
                        ].map((f) => (
                          <button
                            key={f.value}
                            onClick={() => setNpsFilter(f.value)}
                            className={`px-3 py-1.5 text-xs rounded-md transition-all flex items-center gap-1.5 ${
                              npsFilter === f.value
                                ? "bg-white shadow-sm text-slate-900 font-medium"
                                : "text-slate-600 hover:text-slate-900"
                            }`}
                          >
                            {f.color && <div className={`w-2 h-2 rounded-full ${f.color}`} />}
                            {f.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {npsData?.submissions?.length > 0 ? (
                    npsData.submissions
                      .filter(sub => {
                        // User type filter
                        if (npsUserTypeFilter !== "all") {
                          const userType = (sub.user_type || "").toLowerCase();
                          if (userType !== npsUserTypeFilter) return false;
                        }
                        // Sentiment filter
                        if (npsFilter !== "all" && sub.sentiment !== npsFilter) return false;
                        return true;
                      })
                      .map((sub) => (
                        <FeedbackCard 
                          key={sub.id} 
                          submission={sub} 
                          surveyType="NPS" 
                          feedbackNotes={feedbackNotes}
                          onNotesUpdate={fetchFeedbackNotes}
                        />
                      ))
                  ) : (
                    <p className="text-center text-slate-500 py-8">No NPS responses for this period</p>
                  )}
                  {npsData?.submissions?.length > 0 && 
                   npsData.submissions.filter(sub => {
                     if (npsUserTypeFilter !== "all") {
                       const userType = (sub.user_type || "").toLowerCase();
                       if (userType !== npsUserTypeFilter) return false;
                     }
                     if (npsFilter !== "all" && sub.sentiment !== npsFilter) return false;
                     return true;
                   }).length === 0 && (
                    <p className="text-center text-slate-500 py-8">No responses match the selected filters</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CSAT Tab */}
          <TabsContent value="csat" className="mt-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Customer Satisfaction</h2>
                <p className="text-slate-500">Email Customer Support Survey</p>
              </div>
              <div className="flex items-center gap-3">
                <Select value={csatPeriod} onValueChange={setCsatPeriod}>
                  <SelectTrigger className="w-[180px] bg-white">
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="last_30_days">Last 30 Days</SelectItem>
                    <SelectItem value="last_3_months">Last 3 Months</SelectItem>
                    <SelectItem value="last_6_months">Last 6 Months</SelectItem>
                    <SelectItem value="last_12_months">Last 12 Months</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={fetchCsatData} className="border-slate-200">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* CSAT Score Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="bg-white border-slate-200 shadow-sm col-span-1">
                <CardContent className="p-6 flex flex-col items-center justify-center">
                  <ScoreGauge score={csatData?.score || 0} label="CSAT %" maxScore={100} color="emerald" />
                  {csatData?.comparison?.change !== undefined && (
                    <ComparisonBadge 
                      change={csatData.comparison.change} 
                      label={csatData.comparison.previous_period?.label || "previous"} 
                    />
                  )}
                  <p className="text-sm text-slate-500 mt-2">{csatData?.total || 0} total responses</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200 shadow-sm col-span-1 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-900">Sentiment Breakdown</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <SmilePlus className="w-4 h-4 text-emerald-500" />
                        Positive
                      </span>
                      <span className="font-medium">{csatData?.positive || 0}</span>
                    </div>
                    <Progress value={csatData?.total ? (csatData.positive / csatData.total) * 100 : 0} className="h-2 bg-slate-100" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <Meh className="w-4 h-4 text-amber-500" />
                        Neutral
                      </span>
                      <span className="font-medium">{csatData?.neutral || 0}</span>
                    </div>
                    <Progress value={csatData?.total ? (csatData.neutral / csatData.total) * 100 : 0} className="h-2 bg-slate-100" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <Frown className="w-4 h-4 text-red-500" />
                        Negative
                      </span>
                      <span className="font-medium">{csatData?.negative || 0}</span>
                    </div>
                    <Progress value={csatData?.total ? (csatData.negative / csatData.total) * 100 : 0} className="h-2 bg-slate-100" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* CSAT Trend Charts */}
            <TrendCharts trends={csatTrends} surveyType="csat" />

            {/* CSAT Responses */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-[#425B76]" />
                    Recent CSAT Responses
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">Filter:</span>
                    <div className="flex bg-slate-100 rounded-lg p-1 gap-1">
                      {[
                        { value: "all", label: "All" },
                        { value: "positive", label: "Positive", icon: SmilePlus, color: "text-emerald-500" },
                        { value: "neutral", label: "Neutral", icon: Meh, color: "text-amber-500" },
                        { value: "negative", label: "Negative", icon: Frown, color: "text-red-500" }
                      ].map((f) => (
                        <button
                          key={f.value}
                          onClick={() => setCsatFilter(f.value)}
                          className={`px-3 py-1.5 text-xs rounded-md transition-all flex items-center gap-1.5 ${
                            csatFilter === f.value
                              ? "bg-white shadow-sm text-slate-900 font-medium"
                              : "text-slate-600 hover:text-slate-900"
                          }`}
                        >
                          {f.icon && <f.icon className={`w-3.5 h-3.5 ${f.color}`} />}
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {csatData?.submissions?.length > 0 ? (
                    csatData.submissions
                      .filter(sub => csatFilter === "all" || sub.sentiment === csatFilter)
                      .map((sub) => (
                        <FeedbackCard 
                          key={sub.id} 
                          submission={sub} 
                          surveyType="CSAT" 
                          feedbackNotes={feedbackNotes}
                          onNotesUpdate={fetchFeedbackNotes}
                        />
                      ))
                  ) : (
                    <p className="text-center text-slate-500 py-8">No CSAT responses for this period</p>
                  )}
                  {csatData?.submissions?.length > 0 && 
                   csatData.submissions.filter(sub => csatFilter === "all" || sub.sentiment === csatFilter).length === 0 && (
                    <p className="text-center text-slate-500 py-8">No {csatFilter} responses found</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default SatisfactionPage;
